import { type Control } from "react-hook-form";
import { Stack, TextField } from "@mui/material";
import { z } from "zod";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import * as FormDialog from "./FormDialog";

export type UserFormValues = { email: string; firstName: string; lastName: string };

type UserFieldProps = { control: Control<UserFormValues>; disabled?: boolean };

export const EmailField = ({ control, disabled }: UserFieldProps) => {
  const { t } = useTranslation();

  return (
    <FormDialog.ControlledField
      control={control}
      name="email"
      rules={{ validate: value => z.email().safeParse(value).success || t("Input.Validation.RequiredEmail") }}
      label={t("CreateUser.Dialog.Form.EmailLabel")}
    >
      {({ field: { ref, ...field }, fieldState, id }) => (
        <TextField
          size="small"
          fullWidth
          id={id}
          placeholder={t("CreateUser.Dialog.Form.EmailPlaceholder")}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          {...field}
          onChange={event => field.onChange(event.target.value.trim())}
          inputRef={ref}
        />
      )}
    </FormDialog.ControlledField>
  );
};

export const NameFields = ({ control, disabled }: UserFieldProps) => (
  <Stack spacing={2} direction="row">
    <NameField
      control={control}
      disabled={disabled}
      name="firstName"
      labelKey="CreateUser.Dialog.Form.FirstnameLabel"
      placeholderKey="CreateUser.Dialog.Form.FirstnamePlaceholder"
    />
    <NameField
      control={control}
      disabled={disabled}
      name="lastName"
      labelKey="CreateUser.Dialog.Form.LastnameLabel"
      placeholderKey="CreateUser.Dialog.Form.LastnamePlaceholder"
    />
  </Stack>
);

const NameField = ({
  control,
  disabled,
  name,
  labelKey,
  placeholderKey,
}: UserFieldProps & {
  name: "firstName" | "lastName";
  labelKey: TranslationKey;
  placeholderKey: TranslationKey;
}) => {
  const { t } = useTranslation();

  return (
    <FormDialog.TextField
      control={control}
      name={name}
      rules={{ required: t("Input.Validation.RequiredName") }}
      label={t(labelKey)}
      placeholder={t(placeholderKey)}
      disabled={disabled}
    />
  );
};
